import {
  ChevronDown,
  FileText,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  ShieldCheck,
  Sparkles,
  Undo2,
} from 'lucide-react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from '../../../services/api/client'
import { Button } from '../../../ui/Button/Button'
import { Spinner } from '../../../ui/Spinner/Spinner'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import {
  conversationFacts,
  finalFacts,
  groupCalls,
  hasTimeline,
  totalDurationSeconds,
  type CallGroup,
} from '../call-timeline'
import {
  appointmentNote,
  callbackEventCopy,
  callerNeedCopy,
  conversationOutcome,
  ownerEventCopy,
  transferEventCopy,
  type TransferEventCopy,
} from '../handoff-presentation'
import type {
  CallEvent,
  CallTimelineFacts,
  CallTranscript,
  InteractionCall,
} from '../interaction-calls-contracts'
import type { InteractionCallsRepository } from '../interaction-calls-repository'
import { useInteractionCalls } from '../use-interaction-calls'
import styles from '../ConversationsPage.module.css'
import { HandoffBadge, IntentBadge } from './IntentBadge'

function formatCallTime(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp)
}

function formatDuration(seconds: number) {
  const rounded = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(rounded / 60)
  const remainder = rounded % 60
  return `${minutes}:${remainder.toString().padStart(2, '0')}`
}

function directionLabel(call: InteractionCall) {
  if (call.direction === 'INCOMING') return 'Incoming call'
  if (call.direction === 'OUTGOING') return 'Outgoing call'
  return 'Voice call'
}

function endLabel(call: InteractionCall) {
  if (call.endReason === 'CALLER_ENDED') return 'Caller ended'
  if (call.endReason === 'ASSISTANT_ENDED') return 'Assistant ended'
  return 'Ended'
}

type LazyState<T> =
  | { status: 'idle' | 'loading' | 'unavailable' | 'error'; value: null }
  | { status: 'ready'; value: T }

function useLazyArtifact<T>({
  owner,
  loadArtifact,
}: {
  owner: string
  loadArtifact: (signal: AbortSignal) => Promise<T>
}) {
  const generation = useRef(0)
  const controller = useRef<AbortController | null>(null)
  const [state, setState] = useState<LazyState<T>>({
    status: 'idle',
    value: null,
  })

  useEffect(() => {
    generation.current += 1
    controller.current?.abort()
    setState({ status: 'idle', value: null })
    return () => {
      generation.current += 1
      controller.current?.abort()
    }
  }, [owner])

  const load = useCallback(() => {
    const currentGeneration = ++generation.current
    controller.current?.abort()
    const currentController = new AbortController()
    controller.current = currentController
    setState({ status: 'loading', value: null })
    void loadArtifact(currentController.signal)
      .then((value) => {
        if (generation.current !== currentGeneration) return
        setState({ status: 'ready', value })
      })
      .catch((error: unknown) => {
        if (generation.current !== currentGeneration) return
        setState({
          status:
            error instanceof ApiError && error.status === 404
              ? 'unavailable'
              : 'error',
          value: null,
        })
      })
  }, [loadArtifact])

  return { ...state, load }
}

function SecureRecording({
  tenantId,
  aggregateId,
  callRef,
  repository,
}: {
  tenantId: string
  aggregateId: string
  callRef: string
  repository: InteractionCallsRepository
}) {
  const objectUrl = useRef<string | null>(null)
  const artifact = useLazyArtifact({
    owner: `${tenantId}:${aggregateId}:${callRef}`,
    loadArtifact: useCallback(
      (signal: AbortSignal) =>
        repository.getRecording(tenantId, aggregateId, callRef, signal),
      [aggregateId, callRef, repository, tenantId]
    ),
  })
  const loadRecording = artifact.load
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [playbackError, setPlaybackError] = useState(false)

  useEffect(() => loadRecording(), [loadRecording])

  useEffect(() => {
    if (artifact.status !== 'ready') {
      setAudioUrl(null)
      return
    }
    const nextUrl = URL.createObjectURL(artifact.value)
    objectUrl.current = nextUrl
    setAudioUrl(nextUrl)
    setPlaybackError(false)
    return () => {
      URL.revokeObjectURL(nextUrl)
      if (objectUrl.current === nextUrl) objectUrl.current = null
    }
  }, [artifact.status, artifact.value])

  return (
    <section className={styles.callArtifact} aria-label="Call recording">
      <div className={styles.artifactHeading}>
        <span className={styles.callArtifactIcon} aria-hidden="true">
          <Play size={14} />
        </span>
        <div>
          <h4>Recording</h4>
          <p>Loaded securely for this expanded call.</p>
        </div>
      </div>
      {artifact.status === 'loading' ? (
        <Spinner label="Loading call recording" />
      ) : null}
      {artifact.status === 'ready' && audioUrl ? (
        <>
          <audio
            className={styles.secureAudio}
            aria-label="Secure call recording"
            controls
            preload="metadata"
            src={audioUrl}
            onError={() => setPlaybackError(true)}
          />
          {playbackError ? (
            <p role="alert">The recording could not be played.</p>
          ) : null}
        </>
      ) : null}
      {artifact.status === 'unavailable' ? <p>Recording unavailable.</p> : null}
      {artifact.status === 'error' ? (
        <div className={styles.artifactRetry}>
          <p role="alert">The recording could not be loaded.</p>
          <Button variant="secondary" type="button" onClick={loadRecording}>
            Retry
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function TranscriptLines({
  segments,
  events = [],
}: {
  segments: CallTranscript['segments']
  events?: CallEvent[]
}) {
  // What the owners did reads in place, where its time is known; the rest follows.
  const items: Array<
    | { key: string; segment: CallTranscript['segments'][number] }
    | { key: string; event: CallEvent }
  > = []
  let next = 0
  segments.forEach((segment, index) => {
    while (
      segment.offsetMs !== undefined &&
      events[next]?.offsetMs !== undefined &&
      events[next]!.offsetMs! <= segment.offsetMs
    ) {
      items.push({ key: `event-${next}`, event: events[next]! })
      next += 1
    }
    items.push({
      key: `${segment.speaker}-${segment.offsetMs ?? index}-${index}`,
      segment,
    })
  })
  for (; next < events.length; next += 1) {
    items.push({ key: `event-${next}`, event: events[next]! })
  }
  let lastText: string | null = null
  return (
    <ol className={styles.transcriptList} aria-label="Call transcript">
      {items.map((item) => {
        if ('event' in item) {
          const copy = ownerEventCopy(item.event)
          // The same answer given again adds nothing.
          if (!copy || copy.text === lastText) return null
          lastText = copy.text
          return (
            <li
              key={item.key}
              className={`${styles.timelineInline} ${styles[`timelineInline-${copy.tone}`]}`}
            >
              {copy.text}
            </li>
          )
        }
        const { segment } = item
        return (
          <li key={item.key}>
            <strong>
              {segment.speaker === 'PATIENT' ? 'Patient' : 'Flio AI'}
            </strong>
            <p>{segment.text}</p>
            {segment.offsetMs !== undefined ? (
              <time>{formatDuration(segment.offsetMs / 1000)}</time>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

function Transcript({
  tenantId,
  aggregateId,
  callRef,
  repository,
}: {
  tenantId: string
  aggregateId: string
  callRef: string
  repository: InteractionCallsRepository
}) {
  const [open, setOpen] = useState(false)
  const artifact = useLazyArtifact<CallTranscript>({
    owner: `${tenantId}:${aggregateId}:${callRef}`,
    loadArtifact: useCallback(
      (signal: AbortSignal) =>
        repository.getTranscript(tenantId, aggregateId, callRef, signal),
      [aggregateId, callRef, repository, tenantId]
    ),
  })

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && artifact.status === 'idle') artifact.load()
  }

  return (
    <section className={styles.callArtifact}>
      <button
        className={styles.artifactDisclosure}
        type="button"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className={styles.callArtifactIcon} aria-hidden="true">
          <FileText size={14} />
        </span>
        <span>
          <strong>Transcript</strong>
          <small>Patient and Flio AI dialogue only</small>
        </span>
        <ChevronDown
          className={open ? styles.disclosureOpen : ''}
          size={16}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className={styles.artifactBody}>
          {artifact.status === 'loading' ? (
            <Spinner label="Loading call transcript" />
          ) : null}
          {artifact.status === 'ready' && artifact.value.segments.length ? (
            <TranscriptLines
              segments={artifact.value.segments.filter(
                (segment) => segment.audience !== 'FRONT_DESK'
              )}
            />
          ) : null}
          {artifact.status === 'ready' && !artifact.value.segments.length ? (
            <p>No transcript segments are available.</p>
          ) : null}
          {artifact.status === 'unavailable' ? (
            <p>Transcript unavailable.</p>
          ) : null}
          {artifact.status === 'error' ? (
            <div className={styles.artifactRetry}>
              <p role="alert">The transcript could not be loaded.</p>
              <Button variant="secondary" type="button" onClick={artifact.load}>
                Retry
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function CallDetails({
  call,
  tenantId,
  aggregateId,
  repository,
  showTranscript = true,
}: {
  call: InteractionCall
  tenantId: string
  aggregateId: string
  repository: InteractionCallsRepository
  showTranscript?: boolean
}) {
  return (
    <div className={styles.callCardDetails}>
      <dl className={styles.callFacts}>
        <div>
          <dt>Status</dt>
          <dd>Ended</dd>
        </div>
        <div>
          <dt>Direction</dt>
          <dd>{directionLabel(call).replace(' call', '')}</dd>
        </div>
        <div>
          <dt>Duration</dt>
          <dd>{formatDuration(call.durationSeconds)}</dd>
        </div>
      </dl>
      <section className={styles.callSummary}>
        <div className={styles.artifactHeading}>
          <span className={styles.callArtifactIcon} aria-hidden="true">
            <Sparkles size={14} />
          </span>
          <div>
            <h4>Call summary {call.classification === 'CALLER_MESSAGE' && <span aria-label="Caller message">Message</span>}</h4>
            <p>Provider-derived interaction context</p>
          </div>
        </div>
        {call.savedMessage && <section aria-label="Saved caller message">
          <h5>Saved message</h5>
          <p style={{ whiteSpace: 'pre-wrap' }}>{call.savedMessage}</p>
          <small>Saved for the clinic; not evidence of staff read or callback.</small>
        </section>}
        <p>{call.summary ?? 'No call summary is available.'}</p>
        <small>
          Not authoritative patient, appointment, or PMS outcome.
        </small>
      </section>
      {call.recordingAvailable ? (
        <SecureRecording
          tenantId={tenantId}
          aggregateId={aggregateId}
          callRef={call.callRef}
          repository={repository}
        />
      ) : (
        <section className={styles.callArtifact}>
          <p>Recording unavailable.</p>
        </section>
      )}
      {!showTranscript ? null : call.transcriptAvailable ? (
        <Transcript
          tenantId={tenantId}
          aggregateId={aggregateId}
          callRef={call.callRef}
          repository={repository}
        />
      ) : (
        <section className={styles.callArtifact}>
          <p>Transcript unavailable.</p>
        </section>
      )}
    </div>
  )
}

function CallCard({
  call,
  open,
  onToggle,
  tenantId,
  aggregateId,
  repository,
}: {
  call: InteractionCall
  open: boolean
  onToggle: () => void
  tenantId: string
  aggregateId: string
  repository: InteractionCallsRepository
}) {
  const DirectionIcon =
    call.direction === 'OUTGOING' ? PhoneOutgoing : PhoneIncoming
  const artifactCount = [
    call.recordingAvailable,
    call.transcriptAvailable,
  ].filter(Boolean).length
  return (
    <article className={styles.callCard}>
      <button
        className={styles.callCardButton}
        type="button"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className={styles.callDirectionIcon} aria-hidden="true">
          <DirectionIcon size={16} />
        </span>
        <span className={styles.callCardIdentity}>
          <strong>{directionLabel(call)}</strong>
          <time dateTime={new Date(call.startedAtMs).toISOString()}>
            {formatCallTime(call.startedAtMs)}
          </time>
        </span>
        <span className={styles.callFact}>
          {formatDuration(call.durationSeconds)}
        </span>
        <span className={styles.callFact}>{endLabel(call)}</span>
        <span className={styles.artifactCount}>
          {artifactCount} artifact{artifactCount === 1 ? '' : 's'}
        </span>
        <ChevronDown
          className={open ? styles.disclosureOpen : ''}
          size={17}
          aria-hidden="true"
        />
        {call.timeline?.intent ? (
          <span className={styles.callCardBadges}>
            <IntentBadge intent={call.timeline.intent} prominent />
          </span>
        ) : null}
      </button>
      {open ? (
        <CallDetails
          call={call}
          tenantId={tenantId}
          aggregateId={aggregateId}
          repository={repository}
        />
      ) : null}
    </article>
  )
}

function formatEventTime(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(timestamp)
}

function TimelineEventCard({
  copy,
  facts = [],
  announcement,
}: {
  copy: TransferEventCopy
  facts?: string[]
  announcement?: string
}) {
  const Icon = copy.icon
  return (
    <section
      className={`${styles.timelineEvent} ${styles[`timelineEvent-${copy.tone}`]}`}
      aria-label={copy.heading}
      data-tone={copy.tone}
    >
      <span className={styles.timelineEventIcon} aria-hidden="true">
        <Icon size={18} />
      </span>
      <div>
        <h4>{copy.heading}</h4>
        <p>{copy.outcome}</p>
        {facts.length ? (
          <ul className={styles.timelineEventFacts}>
            {facts.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        ) : null}
        {announcement ? (
          <figure className={styles.timelineAnnouncement}>
            <figcaption>
              Front desk announcement. Played to clinic staff, not the caller.
            </figcaption>
            <blockquote>{announcement}</blockquote>
          </figure>
        ) : null}
      </div>
    </section>
  )
}

function transferFacts(timeline: CallTimelineFacts) {
  const facts: string[] = []
  if (timeline.releasedAtMs !== undefined) {
    facts.push(`Transfer started ${formatEventTime(timeline.releasedAtMs)}`)
  }
  if (timeline.connectedSeconds !== undefined) {
    facts.push(
      `Connected ${formatDuration(timeline.connectedSeconds)} after the transfer`
    )
  }
  if (timeline.returnedAtMs !== undefined) {
    facts.push(`Front desk line sent it back ${formatEventTime(timeline.returnedAtMs)}`)
  }
  return facts
}

// One leg's words as they happened, with its transfer among them. Only the line
// played to the front desk moves into the transfer's card.
function TimelineLeg({
  call,
  tenantId,
  aggregateId,
  repository,
}: {
  call: InteractionCall
  tenantId: string
  aggregateId: string
  repository: InteractionCallsRepository
}) {
  const artifact = useLazyArtifact<CallTranscript>({
    owner: `${tenantId}:${aggregateId}:${call.callRef}`,
    loadArtifact: useCallback(
      (signal: AbortSignal) =>
        repository.getTranscript(tenantId, aggregateId, call.callRef, signal),
      [aggregateId, call.callRef, repository, tenantId]
    ),
  })
  const load = artifact.load
  const available = call.transcriptAvailable
  useEffect(() => {
    if (available) load()
  }, [available, load])
  const segments = artifact.status === 'ready' ? artifact.value.segments : []
  const events = artifact.status === 'ready' ? (artifact.value.events ?? []) : []
  const at = segments.findIndex((segment) => segment.audience === 'FRONT_DESK')
  const before = at === -1 ? segments : segments.slice(0, at)
  const after =
    at === -1
      ? []
      : segments
          .slice(at + 1)
          .filter((segment) => segment.audience !== 'FRONT_DESK')
  // Events keep their place around the transfer, when the announcement's time is known.
  const splitAt = at === -1 ? undefined : segments[at]?.offsetMs
  const eventsBefore =
    splitAt === undefined
      ? events
      : events.filter(
          (event) => event.offsetMs === undefined || event.offsetMs <= splitAt
        )
  const eventsAfter =
    splitAt === undefined
      ? []
      : events.filter(
          (event) => event.offsetMs !== undefined && event.offsetMs > splitAt
        )
  const timeline = call.timeline
  const deskInPlace = events.some(
    (event) => event.kind === 'FRONT_DESK' && event.result === 'UNAVAILABLE'
  )
  const copy =
    timeline &&
    !timeline.returnOfCallRef &&
    !(timeline.state === 'UNAVAILABLE' && deskInPlace)
      ? transferEventCopy(timeline, call.startedAtMs)
      : null
  return (
    <>
      {artifact.status === 'loading' ? (
        <Spinner label="Loading call transcript" />
      ) : null}
      {artifact.status === 'error' ? (
        <div className={styles.artifactRetry}>
          <p role="alert">The transcript could not be loaded.</p>
          <Button variant="secondary" type="button" onClick={artifact.load}>
            Retry
          </Button>
        </div>
      ) : null}
      {!available || artifact.status === 'unavailable' ? (
        <p className={styles.timelineNote}>Transcript unavailable.</p>
      ) : null}
      {before.length || eventsBefore.length ? (
        <TranscriptLines segments={before} events={eventsBefore} />
      ) : null}
      {copy && timeline ? (
        <TimelineEventCard
          copy={copy}
          facts={transferFacts(timeline)}
          announcement={at === -1 ? undefined : segments[at]?.text}
        />
      ) : null}
      {after.length || eventsAfter.length ? (
        <TranscriptLines segments={after} events={eventsAfter} />
      ) : null}
    </>
  )
}

function CallLeg({
  call,
  index,
  tenantId,
  aggregateId,
  repository,
}: {
  call: InteractionCall
  index: number
  tenantId: string
  aggregateId: string
  repository: InteractionCallsRepository
}) {
  const [open, setOpen] = useState(false)
  return (
    <section className={styles.callArtifact}>
      <button
        className={styles.artifactDisclosure}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={styles.callArtifactIcon} aria-hidden="true">
          <Play size={14} />
        </span>
        <span>
          <strong>Call leg {index + 1}</strong>
          <small>
            {formatCallTime(call.startedAtMs)} · {formatDuration(call.durationSeconds)} ·
            {' '}{endLabel(call)}
          </small>
        </span>
        <ChevronDown
          className={open ? styles.disclosureOpen : ''}
          size={16}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <CallDetails
          call={call}
          tenantId={tenantId}
          aggregateId={aggregateId}
          repository={repository}
          showTranscript={false}
        />
      ) : null}
    </section>
  )
}

function CallGroupCard({
  group,
  open,
  onToggle,
  tenantId,
  aggregateId,
  repository,
}: {
  group: CallGroup
  open: boolean
  onToggle: () => void
  tenantId: string
  aggregateId: string
  repository: InteractionCallsRepository
}) {
  const first = group.legs[0]!
  const latest = group.legs.at(-1)!
  const facts = finalFacts(group)
  const account = conversationFacts(facts)
  const outcome = conversationOutcome(account, { atMs: latest.startedAtMs })
  const need = callerNeedCopy(account)
  const appointment = appointmentNote([facts?.intent, ...(facts?.secondary ?? [])])
  const DirectionIcon =
    first.direction === 'OUTGOING' ? PhoneOutgoing : PhoneIncoming
  // The transfer came back but the returned call isn't listed (the caller said
  // nothing on it, or it's still processing): the separator still tells what happened.
  const returnedWithoutLeg =
    group.legs.length === 1 &&
    first.timeline?.state === 'RETURNED' &&
    !first.timeline.returnOfCallRef
  return (
    <article className={styles.callCard}>
      <button
        className={styles.callCardButton}
        type="button"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className={styles.callDirectionIcon} aria-hidden="true">
          <DirectionIcon size={16} />
        </span>
        <span className={styles.callCardIdentity}>
          <strong>{directionLabel(first)}</strong>
          <time dateTime={new Date(first.startedAtMs).toISOString()}>
            {formatCallTime(first.startedAtMs)}
          </time>
        </span>
        <span className={styles.callFact}>
          {formatDuration(totalDurationSeconds(group))}
        </span>
        {group.legs.length > 1 ? (
          <span className={styles.artifactCount}>
            {group.legs.length} call legs
          </span>
        ) : (
          <span className={styles.callFact}>{endLabel(first)}</span>
        )}
        <ChevronDown
          className={open ? styles.disclosureOpen : ''}
          size={17}
          aria-hidden="true"
        />
        <span className={styles.callCardBadges}>
          {facts?.intent ? <IntentBadge intent={facts.intent} prominent /> : null}
          {outcome?.label ? (
            <HandoffBadge presentation={outcome} label={outcome.label} />
          ) : null}
        </span>
      </button>
      {open ? (
        <div className={styles.callCardDetails}>
          <div className={styles.callTimeline} aria-label="Call timeline">
            {group.earlierCallNotLoaded ? (
              <>
                <p className={styles.timelineNote}>
                  The earlier part of this call is in older calls. Load older
                  calls to see it.
                </p>
                <ReturnSeparator />
              </>
            ) : null}
            {group.legs.map((call, index) => (
              <Fragment key={call.callRef}>
                {index > 0 ? <ReturnSeparator /> : null}
                <TimelineLeg
                  call={call}
                  tenantId={tenantId}
                  aggregateId={aggregateId}
                  repository={repository}
                />
              </Fragment>
            ))}
            {returnedWithoutLeg ? (
              <>
                <ReturnSeparator />
                <p className={styles.timelineNote}>
                  No further conversation is recorded after the return.
                </p>
              </>
            ) : null}
            {facts?.callback === 'SAVED' ||
            facts?.callback === 'PENDING' ||
            facts?.callback === 'NOT_SAVED' ? (
              <TimelineEventCard copy={callbackEventCopy(facts.callback)} />
            ) : null}
            {outcome || facts?.intent ? (
              <div className={styles.timelineFinal}>
                <strong>Final status</strong>
                {facts?.intent ? (
                  <IntentBadge intent={facts.intent} prominent />
                ) : null}
                {outcome?.label ? (
                  <HandoffBadge presentation={outcome} label={outcome.label} />
                ) : null}
                {outcome ? <p>{outcome.description}</p> : null}
                {appointment ? <p>Appointment: {appointment}</p> : null}
                {need ? (
                  <p>
                    Caller need: {need.label}.
                    {need.description === outcome?.description
                      ? ''
                      : ` ${need.description}`}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className={styles.callLegs} aria-label="Call legs">
            {group.legs.map((call, index) => (
              <CallLeg
                key={call.callRef}
                call={call}
                index={index}
                tenantId={tenantId}
                aggregateId={aggregateId}
                repository={repository}
              />
            ))}
          </div>
        </div>
      ) : null}
    </article>
  )
}

function ReturnSeparator() {
  return (
    <p className={styles.timelineSeparator} role="separator">
      <Undo2 size={14} aria-hidden="true" />
      Returned to Ava — Front Desk Unavailable
    </p>
  )
}

export type CallsViewCurrency = {
  owner: string
  currentThroughActivityAtMs: number
}

export function InteractionCallsPane({
  tenantId,
  aggregateId,
  repository,
  active,
  identityLoading = false,
  activityAtMs = null,
  onCurrentChange,
}: {
  tenantId: string
  aggregateId: string | null
  repository: InteractionCallsRepository
  active: boolean
  identityLoading?: boolean
  activityAtMs?: number | null
  onCurrentChange?: (view: CallsViewCurrency | null) => void
}) {
  const calls = useInteractionCalls(
    repository,
    tenantId,
    aggregateId,
    active,
    activityAtMs
  )
  const callsOwner = `${tenantId}:${aggregateId ?? 'unresolved'}`
  const [openCall, setOpenCall] = useState<{
    owner: string
    callRef: string
  } | null>(null)
  const openCallRef = openCall?.owner === callsOwner ? openCall.callRef : null

  useEffect(() => setOpenCall(null), [callsOwner])

  // Report only what is displayed now; a failed or unmounted view retracts it.
  const currentThroughActivityAtMs = calls.currentThroughActivityAtMs
  useEffect(() => {
    if (!onCurrentChange) return
    onCurrentChange(
      currentThroughActivityAtMs === null
        ? null
        : { owner: callsOwner, currentThroughActivityAtMs }
    )
    return () => onCurrentChange(null)
  }, [callsOwner, currentThroughActivityAtMs, onCurrentChange])

  if (!aggregateId && identityLoading) {
    return (
      <div className={styles.callsState}>
        <Spinner label="Resolving canonical call history" />
      </div>
    )
  }
  if (!aggregateId) {
    return (
      <div className={styles.callsState}>
        <StatePanel
          title="Calls unavailable"
          description="Canonical call history becomes available after this conversation has an exact Interaction Aggregate identity."
        />
      </div>
    )
  }
  if (calls.status === 'loading' || calls.status === 'idle') {
    return (
      <div className={styles.callsState}>
        <Spinner label="Loading completed calls" />
      </div>
    )
  }
  if (calls.status === 'error') {
    return (
      <div className={styles.callsState}>
        <StatePanel
          kind="error"
          title="Calls unavailable"
          description="Completed calls could not be loaded. Try again."
        />
        <Button variant="secondary" type="button" onClick={calls.retry}>
          Retry
        </Button>
      </div>
    )
  }
  if (calls.calls.length === 0) {
    return (
      <div className={styles.callsState}>
        <span className={styles.callsEmptyIcon} aria-hidden="true">
          <PhoneIncoming size={20} />
        </span>
        <h3>No completed calls</h3>
        <p>
          Completed Flio Voice calls for this conversation will appear here.
        </p>
      </div>
    )
  }
  return (
    <div className={styles.callsPane}>
      <header className={styles.callsHeader}>
        <div>
          <span className={styles.eyebrow}>Voice history</span>
          <h3>Completed calls</h3>
        </div>
        <span>{calls.calls.length} loaded · newest first</span>
      </header>
      {calls.refreshFailed ? (
        <p role="status">
          Calls could not be refreshed. Showing the last loaded calls.
        </p>
      ) : null}
      <div className={styles.callList}>
        {groupCalls(calls.calls).map((group) => {
          const toggle = () =>
            setOpenCall((current) =>
              current?.owner === callsOwner && current.callRef === group.key
                ? null
                : { owner: callsOwner, callRef: group.key }
            )
          return hasTimeline(group) ? (
            <CallGroupCard
              key={group.key}
              group={group}
              open={openCallRef === group.key}
              onToggle={toggle}
              tenantId={tenantId}
              aggregateId={aggregateId}
              repository={repository}
            />
          ) : (
            <CallCard
              key={group.key}
              call={group.legs[0]!}
              open={openCallRef === group.key}
              onToggle={toggle}
              tenantId={tenantId}
              aggregateId={aggregateId}
              repository={repository}
            />
          )
        })}
      </div>
      {calls.hasOlder ? (
        <Button
          className={styles.loadOlderCalls}
          variant="secondary"
          type="button"
          disabled={calls.loadingOlder}
          onClick={() => void calls.loadOlder()}
        >
          {calls.loadingOlder ? 'Loading older calls…' : 'Load older calls'}
        </Button>
      ) : null}
      <p className={styles.callsPrivacy}>
        <ShieldCheck size={13} aria-hidden="true" /> Recordings and transcripts
        are loaded securely on demand.
      </p>
    </div>
  )
}
